import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal, SectionHeading, Overline } from "@/components/Primitives";
import { SERVICES, IMAGES } from "@/data/site";

export default function Services() {
  const navigate = useNavigate();
  const [active, setActive] = useState(null);

  return (
    <div>
      <section className="relative pt-36 pb-20 bg-brand-green overflow-hidden">
        <img src={IMAGES.pest} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading light overline="Our Services" title="Expert care for every kind of pest"
            subtitle="Twelve specialized programs, one licensed and eco-conscious team. Choose a service to see the signs, our process, and request help." />
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => {
              const open = active === s.slug;
              return (
                <Reveal key={s.slug} delay={(i % 3) * 0.06}>
                  <div className={`group bg-white rounded-3xl border transition-[transform,box-shadow,border-color] duration-300 h-full flex flex-col ${open ? "border-brand-gold shadow-xl" : "border-black/5 shadow-sm hover:shadow-lg hover:-translate-y-1"}`}>
                    <div className="p-7">
                      <div className="h-14 w-14 rounded-2xl bg-brand-green/5 flex items-center justify-center group-hover:bg-brand-green transition-colors duration-300">
                        <s.icon className="h-7 w-7 text-brand-green group-hover:text-brand-gold transition-colors duration-300" />
                      </div>
                      <h3 className="font-heading text-xl font-bold text-brand-green mt-5">{s.name}</h3>
                      <p className="text-brand-muted text-sm leading-relaxed mt-2">{s.desc}</p>

                      {open && (
                        <div className="mt-5 space-y-5 animate-fade-up">
                          <div>
                            <Overline>Common Signs</Overline>
                            <ul className="mt-2 space-y-1.5">
                              {s.signs.map((sign) => (
                                <li key={sign} className="flex items-start gap-2 text-sm text-brand-charcoal"><CheckCircle2 className="h-4 w-4 text-brand-gold mt-0.5 shrink-0" />{sign}</li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <Overline>Treatment Process</Overline>
                            <ol className="mt-2 space-y-1.5">
                              {s.process.map((p, k) => (
                                <li key={p} className="flex items-start gap-2 text-sm text-brand-charcoal">
                                  <span className="h-5 w-5 rounded-full bg-brand-green text-white text-xs flex items-center justify-center shrink-0 mt-0.5">{k + 1}</span>{p}
                                </li>
                              ))}
                            </ol>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="mt-auto p-7 pt-0 flex items-center justify-between">
                      <button data-testid={`service-toggle-${s.slug}`} onClick={() => setActive(open ? null : s.slug)}
                        className="text-sm font-semibold text-brand-green hover:text-brand-gold transition-colors">
                        {open ? "Show less" : "Details"}
                      </button>
                      <Button data-testid={`request-service-${s.slug}`} onClick={() => navigate(`/request-inspection?service=${s.slug}`)}
                        className="rounded-full bg-brand-gold text-brand-green font-bold hover:bg-brand-goldHover">
                        Request Service <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-[2rem] bg-brand-green p-10 lg:p-14 text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-white">Not sure which service you need?</h2>
            <p className="text-white/70 mt-3">Request a free inspection and our technicians will diagnose the problem for you.</p>
            <Button onClick={() => navigate("/request-inspection")} className="rounded-full bg-brand-gold text-brand-green font-bold h-14 px-10 mt-8 hover:bg-brand-goldHover">
              Get a Free Inspection <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
